// apps/web/src/pages/auth/MfaEnroll.tsx
//
// Plan 04-12b Task 2 — TOTP MFA enrollment.
//
// FLOW (CONTEXT D-10 sequence):
//   1. On mount: POST /api/auth/two-factor/enable
//      Server (Plan 04-08 mfa.ts) returns:
//        { qrDataUrl, manualSecret, otpauthUri, backupCodes }
//      The Better-Auth backupCodes are returned but Plan 04-09 ships our own
//      argon2id-hashed backup-code substrate; we use the codes Better-Auth
//      returns at enrollment for the "display ONCE" UX.
//   2. Display QR + manual secret + confirm-code input.
//   3. User enters TOTP code → POST /api/auth/two-factor/verify-totp
//   4. On success → display backup codes ONCE + download-as-text-file button
//      + "I've saved them" button that clears codes from React state.
//   5. On wrong code → inline error, QR remains, user can retry.
//
// SECURITY (T-04-12b-01 mitigation):
//   - Backup codes live in React state ONLY. NEVER touch localStorage or
//     sessionStorage. After "I've saved them" → setCodes(null) clears state.
//   - The manualSecret is also React-state-only; the QR data URL is rendered
//     as <img src=...> but the URL itself is never stored anywhere queryable.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "@/lib/api-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface EnrollResult {
  qrDataUrl: string;
  manualSecret: string;
  otpauthUri: string;
  backupCodes: string[];
}

function formatBackupCodesForDownload(codes: string[]): string {
  const ts = new Date().toISOString();
  return [
    "The Recipe Spread — backup codes",
    "==================================",
    "",
    "Keep these codes somewhere safe. Each one can be used ONCE to sign in",
    "if you lose your authenticator app.",
    "",
    ...codes,
    "",
    `Generated at ${ts}`,
  ].join("\n");
}

export function MfaEnroll(): React.ReactElement {
  const navigate = useNavigate();
  const [enrollResult, setEnrollResult] = useState<EnrollResult | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [enrolling, setEnrolling] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const enrolledRef = useRef(false);

  useEffect(() => {
    if (enrolledRef.current) return;
    enrolledRef.current = true;
    void (async () => {
      setEnrollError(null);
      try {
        const result = await api<EnrollResult>(
          "/api/auth/two-factor/enable",
          { method: "POST" },
        );
        setEnrollResult(result);
      } catch (err) {
        if (err instanceof ApiError) {
          setEnrollError(err.message);
        } else {
          setEnrollError(
            err instanceof Error ? err.message : "Could not start MFA enrollment",
          );
        }
      } finally {
        setEnrolling(false);
      }
    })();
  }, []);

  async function onConfirm(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (confirmCode.length === 0) {
      setError("Enter the code from your authenticator app.");
      return;
    }
    setVerifying(true);
    try {
      await api<{ ok: true }>("/api/auth/two-factor/verify-totp", {
        method: "POST",
        body: JSON.stringify({ code: confirmCode }),
      });
      // Verification succeeded — Better-Auth flips user.twoFactorEnabled true
      // and our service emits mfa_setup_completed. Reveal the backup codes
      // that came back from the enable call.
      if (enrollResult !== null) {
        setBackupCodes(enrollResult.backupCodes);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        type ErrBody = { error?: { code?: string; message?: string } };
        const body = err.body as ErrBody | null;
        setError(body?.error?.message ?? "That code didn't match. Try again.");
      } else {
        setError(
          err instanceof Error ? err.message : "Could not verify the code",
        );
      }
    } finally {
      setVerifying(false);
    }
  }

  function onDownloadBackupCodes(): void {
    if (backupCodes === null) return;
    const blob = new Blob([formatBackupCodesForDownload(backupCodes)], {
      type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "recipe-spread-backup-codes.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function onAcknowledgeSaved(): void {
    // SECURITY: clear backup codes from React state so a later React DevTools
    // inspection (or a long-running tab) cannot surface them.
    setBackupCodes(null);
    // Also clear the manualSecret + otpauthUri — enrollment is done.
    setEnrollResult(null);
    navigate("/account/security");
  }

  if (enrolling) {
    return (
      <main className="mx-auto max-w-md p-8">
        <Card>
          <CardHeader>
            <CardTitle>Setting up two-factor authentication</CardTitle>
            <CardDescription>One moment…</CardDescription>
          </CardHeader>
        </Card>
      </main>
    );
  }

  if (enrollError !== null) {
    return (
      <main className="mx-auto max-w-md p-8">
        <Card>
          <CardHeader>
            <CardTitle>Could not start enrollment</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertTitle>Enrollment failed</AlertTitle>
              <AlertDescription>{enrollError}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (enrollResult === null && backupCodes === null) {
    return (
      <main className="mx-auto max-w-md p-8">
        <Card>
          <CardHeader>
            <CardTitle>Two-factor authentication is now active</CardTitle>
          </CardHeader>
        </Card>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Set up two-factor authentication</CardTitle>
          <CardDescription>
            Scan the QR code with an authenticator app (Google Authenticator,
            1Password, Authy, etc.), then enter the code to confirm.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {enrollResult !== null && backupCodes === null ? (
            <>
              <div className="flex justify-center">
                <img
                  src={enrollResult.qrDataUrl}
                  alt="MFA QR code"
                  className="h-48 w-48 rounded-md border"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="manual-secret">Can't scan? Enter this manually</Label>
                <code
                  id="manual-secret"
                  className="block break-all rounded-md bg-muted p-2 font-mono text-sm"
                >
                  {enrollResult.manualSecret}
                </code>
              </div>

              <form onSubmit={onConfirm} className="space-y-4">
                {error !== null ? (
                  <Alert variant="destructive">
                    <AlertTitle>Code didn't match</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : null}
                <div className="space-y-1">
                  <Label htmlFor="confirm-code">Authenticator code</Label>
                  <Input
                    id="confirm-code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={6}
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value)}
                    disabled={verifying}
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={verifying || confirmCode.length === 0}
                >
                  {verifying ? "Verifying…" : "Confirm and enable"}
                </Button>
              </form>
            </>
          ) : null}

          {backupCodes !== null ? (
            <>
              <Alert>
                <AlertTitle>Two-factor authentication enabled</AlertTitle>
                <AlertDescription>
                  Save these backup codes now — each one can be used once to
                  sign in if you lose your authenticator. They won't be shown
                  again.
                </AlertDescription>
              </Alert>
              <pre
                data-testid="backup-codes-block"
                className="overflow-auto rounded-md bg-muted p-4 font-mono text-sm leading-loose"
              >
                {backupCodes.join("\n")}
              </pre>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onDownloadBackupCodes}
                >
                  Download backup codes
                </Button>
                <Button
                  type="button"
                  onClick={onAcknowledgeSaved}
                >
                  I've saved them
                </Button>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </main>
  );
}
